import React, { createContext, useContext } from 'react';
import { cn } from '@/utils/cn';

interface TeeTabsContextValue {
  value: string;
  onChange: (value: string) => void;
}

const TeeTabsContext = createContext<TeeTabsContextValue | null>(null);

function useTeeTabsContext() {
  const ctx = useContext(TeeTabsContext);
  if (!ctx) throw new Error('TeeTabsTrigger must be used inside TeeTabs');
  return ctx;
}

// ─── Root ────────────────────────────────────────────────────────────────────

export interface TeeTabsProps {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

export function TeeTabs({ value, onChange, children, className }: TeeTabsProps) {
  return (
    <TeeTabsContext.Provider value={{ value, onChange }}>
      <div className={cn('flex flex-col', className)}>{children}</div>
    </TeeTabsContext.Provider>
  );
}

// ─── Tab List ────────────────────────────────────────────────────────────────

export interface TeeTabsListProps {
  children: React.ReactNode;
  className?: string;
}

export function TeeTabsList({ children, className }: TeeTabsListProps) {
  return (
    <div
      className={cn(
        'flex flex-row shrink-0 border-b border-[var(--border-default)] overflow-x-auto',
        className
      )}
    >
      {children}
    </div>
  );
}

// ─── Trigger ─────────────────────────────────────────────────────────────────

export interface TeeTabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export function TeeTabsTrigger({
  value,
  children,
  className,
  disabled = false,
}: TeeTabsTriggerProps) {
  const { value: activeValue, onChange } = useTeeTabsContext();
  const isActive = activeValue === value;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      disabled={disabled}
      onClick={() => !disabled && onChange(value)}
      className={cn(
        'relative px-4 py-3 text-body-s whitespace-nowrap transition-colors duration-150 shrink-0',
        'border-b-2 -mb-px focus:outline-none',
        isActive
          ? 'border-[#B58F2D] text-[hsl(var(--foreground))] font-semibold'
          : 'border-transparent text-[var(--text-content-sub-title,#666)] hover:text-[hsl(var(--foreground))] font-normal',
        disabled && 'opacity-40 cursor-not-allowed',
        className
      )}
    >
      {children}
    </button>
  );
}

// ─── Content ─────────────────────────────────────────────────────────────────

export interface TeeTabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function TeeTabsContent({ value, children, className }: TeeTabsContentProps) {
  const { value: activeValue } = useTeeTabsContext();
  if (activeValue !== value) return null;
  return <div className={cn('pt-4', className)}>{children}</div>;
}
